import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    navigation: {
      technology: 'Technology',
      features: 'Features',
      assets: 'Assets',
      swarm: 'Swarm Intelligence',
      learnMore: 'Learn More',
      whitepaper: 'Whitepaper',
      technicalPaper: 'Technical Paper',
      terms: 'Terms',
      github: 'GitHub',
      linkedin: 'LinkedIn',
      backToHome: 'Back to Home'
    },
    
    // Hero Section
    hero: {
      title: 'AI Authentication Protocol for Cultural Heritage',
      subtitle: 'Shenbury provides institutional-grade authentication infrastructure for museums and cultural organizations using advanced AI verification systems.',
      description: 'Our proprietary AI swarm combines computer vision, machine learning, and blockchain verification for unparalleled authenticity assessment.',
      ctaPrimary: 'Explore Assets',
      ctaSecondary: 'View Whitepaper',
      floatingCard: {
        badge: 'AI Verified',
        viewCollection: 'View Collection →'
      },
      trustIndicators: {
        blockchain: 'Blockchain verified',
        security: 'Bank-grade security',
        privacy: 'Data privacy certified'
      },
      stats: {
        artifacts: '12 Verified Artifacts',
        accuracy: '99.8% Accuracy',
        institutions: '50+ Institutions',
        market: '$68B+ Art Market',
        processed: '10,000+ Artifacts Processed',
        optimized: 'AI-Optimized',
        compute: 'Edge Computing'
      }
    },
    
    // Technology Section
    technology: {
      title: 'Advanced Authentication Technology',
      subtitle: 'Our proprietary AI systems combine computer vision, machine learning, and blockchain verification for unparalleled authenticity assessment.',
      description: 'The Shenbury AI-Powered Authentication Protocol: a decentralized, self-contained intelligent infrastructure solving the oracle problem for the global art market through paid verification services.',
      cards: {
        zeroTrust: {
          title: 'Zero Trust Architecture',
          description: 'Multi-layer validation requiring consensus from diverse AI models before authentication confirmation.',
          features: ['Immutable Records', 'Authenticated Communication', 'On-Chain Verification']
        },
        localEmbeddings: {
          title: 'Local Embedding Generation',
          description: 'On-device feature extraction ensures privacy while maintaining authentication accuracy.',
          features: ['Complete Data Privacy', 'No Cloud Dependencies', 'Offline Operation']
        },
        orchestration: {
          title: 'Intelligent Orchestration',
          description: 'Dynamic routing of authentication requests to optimal processing pipelines based on artifact characteristics.',
          features: ['Task Management', 'Agent Coordination', 'Mesh Topology']
        },
        quantx: {
          title: 'Quantum-Resistant Security',
          description: 'Future-proof cryptographic protocols protecting authentication records against quantum computing threats.',
          features: ['Elemental Detection', 'Non-Destructive Testing', 'Historical Data Comparison']
        },
        gnn: {
          title: 'Graph Neural Network',
          description: 'Deep graph-based pattern recognition that maps relational structures between artifacts, materials, and stylistic features for precise authenticity analysis.',
          features: ['Local Execution', 'Specialized Roles', 'Closed-Loop Learning']
        },
        selfOptimizing: {
          title: 'Self-Optimizing Vector Database',
          description: 'Adaptive embedding storage that continuously refines its indexing through learned feedback, ensuring sub-millisecond retrieval with evolving accuracy.',
          features: ['4-32x Compression', 'Sub-millisecond Queries', 'No Cloud Dependencies']
        },
        multiAgent: {
          title: 'Multi-Agent Orchestration',
          description: 'Coordinated swarm of specialized AI agents that cross-validate findings through consensus protocols for robust artifact authentication.',
          features: ['Evaluates Outcomes', 'Extracts Knowledge', 'Weighted Retrieval']
        }
      },
      stats: {
        decentralized: {
          value: 'Decentralized',
          label: 'AI at Scale'
        },
        compression: {
          value: '32x',
          label: 'Compression Ratio'
        },
        dimensions: {
          value: '384',
          label: 'Vector Dimensions'
        },
        cloud: {
          value: 'Zero',
          label: 'Cloud Dependencies'
        }
      }
    },

    // Features Section
    features: {
      title: 'Enterprise Features',
      subtitle: 'Comprehensive authentication solutions designed for institutional collections and professional art markets.',
      cards: {
        aiSwarm: {
          title: 'AI Swarm Analysis',
          description: 'Multiple specialized AI agents analyze artifacts across dimensions including style, materials, and historical context, cross-validating findings through consensus protocols.'
        },
        blockchain: {
          title: 'Blockchain Attestation',
          description: 'Verification results are permanently recorded on-chain, creating an immutable certificate of authenticity with tamper-proof provenance records.'
        },
        apiAccess: {
          title: 'Secure API Access',
          description: 'Enterprise-grade API integration enables seamless access to AI verification services with robust security and authentication controls.'
        },
        zeroTrust: {
          title: 'Zero-Trust Security',
          description: 'All inter-agent communication is authenticated. Security model assumes failure and ensures automated recovery.'
        },
        decentralized: {
          title: 'Decentralized Network',
          description: 'Mesh topology ensures resilience. Single agent failures never halt verification, maintaining 99.99% uptime.'
        },
        learning: {
          title: 'Continuous Learning',
          description: 'Proprietary reasoning engine learns from outcomes, improving accuracy with every verification completed.'
        },
        spectroscopy: {
          title: 'XRF Spectroscopy',
          description: 'QuantX methodology using X-ray Fluorescence to analyze elemental composition. Non-destructive testing compares chemical signatures against historical datasets for authentication.'
        }
      }
    },
    
    // How It Works Section
    howItWorks: {
      badge: 'Verification Process',
      title: 'How Shenbury Works',
      subtitle: 'Our autonomous AI protocol delivers authentication services in four seamless steps, from data submission to immutable blockchain attestation. Access our verification services through our secure platform.',
      steps: {
        upload: {
          title: 'Upload Artifact',
          description: 'Submit high-resolution images and documentation through our secure portal.'
        },
        analyze: {
          title: 'AI Analysis',
          description: 'Multiple neural networks examine materials, construction techniques, and historical markers.'
        },
        verify: {
          title: 'Blockchain Verification',
          description: 'Results are cryptographically sealed and permanently recorded on distributed ledger technology.'
        },
        dataSubmission: {
          title: 'Data Submission',
          description: 'Submit high-resolution images and provenance documentation through our secure upload portal.'
        },
        aiSwarmAnalysis: {
          title: 'AI Swarm Analysis',
          description: 'Multiple specialized AI agents analyze your artifact across dimensions including style, materials, and historical context.'
        },
        consensusLearning: {
          title: 'Consensus Learning',
          description: 'AI agents cross-validate findings and reach consensus through our proprietary swarm intelligence protocol.'
        },
        blockchainAttestation: {
          title: 'Blockchain Attestation',
          description: 'Results are permanently recorded on-chain, creating an immutable certificate of authenticity.'
        }
      },
      ctaReady: 'Ready to access our AI authentication services?',
      ctaLearnMore: 'Learn More',
      ctaReadTechnical: 'Read Technical Paper'
    },
    
    // Relic Gallery
    relicGallery: {
      title: 'Verified Artifact Collection',
      subtitle: 'Explore our curated collection of authenticated historical artifacts with complete provenance records.',
      relics: {
        songWasher: {
          title: 'AN EXCEPTIONALLY RARE RU KILN SUNFLOWER-FORM WASHER',
          chinese: '北宋 汝官窯天青釉葵花洗'
        },
        songBottle: {
          title: 'SONG DYNASTY RU KILN BOTTLE',
          chinese: '宋 汝窯青瓷膽瓶'
        },
        mingCloisonne: {
          title: 'MING DYNASTY CLOISONNÉ ENAMEL JAR',
          chinese: '明 景泰藍花鳥紋梅瓶'
        },
        yuanEwer: {
          title: 'YUAN DYNASTY JILAN EWER',
          chinese: '元 鈞窯玫瑰紫釉花觚'
        },
        mingCasket: {
          title: 'MING DYNASTY JINGTAI CASKET',
          chinese: '明 景泰款掐絲琺琅盒'
        },
        yuanJar: {
          title: 'YUAN DYNASTY CELADON GLAZED JAR',
          chinese: '元 龍泉窯青瓷梅瓶'
        },
        floralVase: {
          title: 'FLORAL HARMONY VASE',
          chinese: '清 珐琅彩花卉紋瓶'
        },
        imperialLotus: {
          title: 'IMPERIAL LOTUS BOTTLE',
          chinese: '清 乾隆款粉彩蓮花紋膽瓶'
        },
        imperialTripod: {
          title: 'IMPERIAL CLOISONNÉ TRIPOD',
          chinese: '明 宣德款掐絲琺琅三足爐'
        },
        junyaoVessel: {
          title: 'JUNYAO OCTAGON VESSEL',
          chinese: '宋 鈞窯天藍釉八方盆'
        },
        yuanBlueWhite: {
          title: 'YUAN BLUE & WHITE PORCELAIN POT',
          chinese: '元 青花雲龍紋梅瓶'
        },
        ruVase: {
          title: 'RU KILN YUHU CHUN VASE',
          chinese: '北宋 汝窯天青釉玉壺春瓶'
        }
      },
      dynasties: {
        northernSong: 'Northern Song Dynasty',
        song: 'Song Dynasty',
        ming: 'Ming Dynasty',
        yuan: 'Yuan Dynasty',
        qing: 'Qing Dynasty'
      }
    },
    
    // Authenticated Relics
    authenticatedRelics: {
      badge: 'Authenticated Collection',
      sectionTitle: 'AI-Verified Artifacts',
      sectionSubtitle: 'Explore artifacts verified by our autonomous AI authentication services, demonstrating the capabilities of our protocol through real-world examples.',
      badges: {
        aiVerified: 'AI Verified',
        blockchain: 'Blockchain',
        verified: 'Verified',
        authenticity: 'Authenticity',
        certified: 'Certified'
      },
      lot: 'Lot #',
      aiService: 'AI Authentication Service',
      demoReport: 'Demo Report',
      accessReport: 'Access Authentication Report',
      detailedAnalysis: 'Detailed Analysis',
      tabs: {
        overview: 'Overview',
        dimensions: 'Dimensions',
        condition: 'Condition',
        significance: 'Significance'
      },
      description: 'Description',
      historicalContext: 'Historical Context',
      measurements: 'Measurements',
      conditionReport: 'Condition Report',
      aiAssessment: 'AI Assessment: Excellent',
      aiAssessmentDesc: 'Our AI analysis confirms exceptional preservation consistent with documented historical examples.',
      culturalSignificance: 'Cultural Significance',
      knownExamples: 'Known Examples Worldwide',
      kilnPeriod: 'Kiln Operation Period',
      dynasty: 'Northern Song Dynasty',
      relic: {
        title: 'AN EXCEPTIONALLY RARE RU KILN SUNFLOWER-FORM WASHER',
        chinese: '北宋 汝官窯天青釉葵花洗',
        dynasty: 'Northern Song Dynasty',
        description: 'This extraordinarily rare washer represents the pinnacle of Song ceramic achievement. The vessel is shaped like a six-part flower mouth with three tiny sesame nail marks on the bottom. The distinctive sky-blue glaze with its subtle crackle pattern was considered the finest of all Chinese ceramics.',
        condition: 'Excellent condition with characteristic ice-crackle throughout the glaze. Minor expected wear to the foot ring consistent with age.',
        culturalSignificance: 'Ru kilns operated for only about 20 years (1086-1106) exclusively for the Northern Song court. Fewer than 100 genuine pieces exist worldwide. A common saying goes: "Even with a fortune, a single piece of Ru porcelain is priceless."',
        historicalContext: 'This washer exemplifies the technical mastery of Ru kiln artisans. The vessel was likely used in imperial ceremonies or as a scholar\'s desk accessory, reflecting the refined culture of the Northern Song court.',
        verificationStatus: 'Blockchain Verified'
      }
    },
    
    // Single Artwork Showcase
    singleArtwork: {
      title: 'AN EXCEPTIONALLY RARE RU KILN SUNFLOWER-FORM WASHER',
      chineseTitle: '北宋 汝官窯天青釉葵花洗',
      dynasty: 'Northern Song Dynasty',
      description: 'This extraordinarily rare washer represents the pinnacle of Song ceramic achievement. The vessel is shaped like a six-part flower mouth with three tiny sesame nail marks on the bottom. The distinctive sky-blue glaze with its subtle crackle pattern was considered the finest of all Chinese ceramics.',
      culturalSignificance: 'Ru kilns operated for only about 20 years (1086-1106) exclusively for the Northern Song court. Fewer than 100 genuine pieces exist worldwide. A common saying goes: "Even with a fortune, a single piece of Ru porcelain is priceless."',
      provenance: 'Previously in the collection of the Palace Museum, Beijing. Acquired by private collector in 2019 through Sotheby\'s Hong Kong.',
      condition: 'Excellent condition with characteristic ice-crackle throughout the glaze. Minor expected wear to the foot ring consistent with age.',
      save: 'Save',
      share: 'Share',
      authentication: {
        title: 'Authentication Details',
        method: 'Multi-AI Consensus Verification',
        confidence: 'Confidence Score',
        date: 'Verification Date',
        verifiedBy: 'Verified by Shenbury AI Network'
      },
      descriptionTitle: 'Artifact Description',
      provenanceTitle: 'Provenance',
      conditionTitle: 'Condition Report',
      culturalSignificanceTitle: 'Cultural Significance'
    },
    
    // Footer
    footer: {
      product: 'Product',
      resources: 'Resources',
      copyright: '© {year} Sheboli Digital Technology Company Ltd. All rights reserved.',
      disclaimer: 'Disclaimer: This is a technical infrastructure project providing AI authentication services for cultural heritage verification.',
      description: 'AI-powered authentication infrastructure for museums and cultural institutions. Shenbury'
    },
    
    // Whitepaper
    whitepaper: {
      title: 'Shenbury Whitepaper',
      researchEdition: 'Research Edition',
      version: 'Version 1.2',
      year: '2026',
      downloadPDF: 'Download PDF',
      viewOnGitHub: 'View on GitHub',
      content: {
        abstract: {
          heading: 'Abstract',
          paragraphs: [
            '**Shenbury** introduces a decentralized, self-contained intelligence infrastructure for authenticating and verifying high-value artifacts. At its core, Shenbury\'s **AI Agent Swarm** combines local vector reasoning, closed-loop learning, and cryptographic authentication services. Enhanced with **proprietary Graph Neural Networks and Self-Optimizing architecture**, each agent features self-improving capabilities that learn from every verification interaction. The system operates independently using a fully local stack — database, embedding, and learning layers — with no reliance on external APIs or cloud systems.',
            'This document formalizes Shenbury\'s mathematical foundations, learning dynamics, and utility-based service architecture designed for institutional authentication markets.'
          ]
        },
        problemStatement: {
          heading: '1. Problem Statement',
          paragraphs: [
            'The global art market exceeds **$60 billion annually** but remains structurally illiquid and opaque. Authentication — the verified history of ownership and authenticity — dictates value but is hindered by centralized trust models and subjective expertise.',
            'For art assets, authentication certainty (P\u2090) and verification confidence (A\u2090) are highly uncertain, resulting in suppressed liquidity and inflated risk premiums. Shenbury increases both via **decentralized, self-learning AI agents enhanced with proprietary GNN technology**, operating under verifiable cryptographic consensus with continuous improvement capabilities.'
          ]
        },
        systemArchitecture: {
          heading: '2. System Architecture Overview',
          intro: 'The Shenbury stack integrates five primary layers:',
          layers: [
            { num: '1', title: 'Enhanced Vector Intelligence Core', desc: 'Local high-performance vector database with proprietary GNN learning' },
            { num: '2', title: 'Enhanced Reasoning Engine', desc: 'Closed-loop reinforcement with trajectory learning and pattern recognition' },
            { num: '3', title: 'Agentic-Flow', desc: 'Multi-agent orchestration and adaptive task allocation' },
            { num: '4', title: 'Claude-Flow', desc: 'Distributed memory and swarm synchronization' },
            { num: '5', title: 'Authentication Chain', desc: 'Blockchain registry of authenticated assets' }
          ]
        },
        agentDB: {
          heading: '3. AgentDB \u2014 Local Vector Intelligence Core',
          paragraphs: [
            'Enhanced database provides semantic memory and retrieval operations at **sub-millisecond latency**, powered by **proprietary Graph Neural Networks**. Each item is embedded as a 384-dimensional vector using a local processing model with attention-weighted embeddings.',
            'Vector quantization achieves **4-32\u00d7 memory compression** while preserving semantic fidelity. The integrated neural layers enable **continuous learning from usage patterns**, making search results improve over time through trajectory-based optimization.'
          ]
        },
        reasoningBank: {
          heading: '4. ReasoningBank \u2014 Closed-Loop Learning System',
          paragraphs: [
            'Enhanced reasoning engine converts inference into a continuous self-improvement process using **proprietary Self-Optimizing Neural Architecture**. For each task, agent belief parameters update based on observed outcomes through trajectory learning, enabling autonomous refinement of heuristics and reliability weights.',
            'This closed feedback loop with **two-tier adaptation and anti-forgetting protection** enables agents to extract reusable knowledge components for future tasks, creating institutional memory without external dependencies while preventing catastrophic forgetting.'
          ]
        },
        memoryRetrieval: {
          heading: '5. Memory Retrieval and Weighted Selection',
          intro: 'Memory retrieval balances four key factors:',
          items: [
            { term: 'Semantic Similarity', desc: 'Relevance between query and memory vectors with attention-weighted scoring' },
            { term: 'Reliability Score', desc: 'Historical accuracy from reasoning engine with trajectory-weighted confidence' },
            { term: 'Recency Decay', desc: 'Time-weighted freshness of observations with learned decay rates' },
            { term: 'Diversity Factor', desc: 'Reduction of redundant retrievals using pattern clustering' },
            { term: 'Pattern Matching', desc: 'Neural-enhanced similarity based on learned verification patterns' }
          ],
          outro: 'This formulation produces robust retrievals for downstream reasoning.'
        },
        agenticFlow: {
          heading: '6. Agentic-Flow \u2014 Multi-Agent Orchestration',
          intro: 'Agentic-Flow coordinates multiple specialized AI agents through dynamic task allocation and load balancing. The system employs **adaptive routing algorithms** that distribute authentication requests based on agent expertise, current workload, and historical performance metrics.',
          componentsIntro: 'Key components include:',
          components: [
            { term: 'Task Router', desc: 'Intelligently distributes incoming verification requests' },
            { term: 'Load Balancer', desc: 'Optimizes resource utilization across agent swarm' },
            { term: 'Performance Monitor', desc: 'Tracks accuracy and response times in real-time' },
            { term: 'Failover Manager', desc: 'Ensures system resilience through automatic redundancy' }
          ],
          outro: 'This orchestration layer enables horizontal scaling while maintaining consistent quality standards across all authentication services.'
        },
        claudeFlow: {
          heading: '7. Claude-Flow \u2014 Distributed Memory Synchronization',
          intro: 'Claude-Flow maintains consistent knowledge states across the distributed agent network through **vector clock synchronization** and conflict resolution protocols. Each agent maintains a local knowledge base that is periodically synchronized with the global swarm memory.',
          mechanismsIntro: 'Core mechanisms include:',
          mechanisms: [
            { term: 'Vector Clocks', desc: 'Track causality relationships between knowledge updates' },
            { term: 'Gossip Protocol', desc: 'Efficient dissemination of new learning across the network' },
            { term: 'Conflict Resolution', desc: 'Merge divergent knowledge states using learned preference models' },
            { term: 'Knowledge Compression', desc: 'Maintain semantic fidelity while reducing storage overhead' }
          ],
          outro: 'This distributed memory system ensures that all agents benefit from collective learning while maintaining operational independence.'
        },
        authenticationChain: {
          heading: '8. Authentication Chain Integration',
          intro: 'The authentication chain provides **immutable proof of verification** for authenticated artifacts. Each successful authentication generates a cryptographic certificate that is anchored to the blockchain, creating an auditable trail of provenance and verification history.',
          integrationIntro: 'Key integration points:',
          integrationPoints: [
            { term: 'Certificate Generation', desc: 'Automated creation of tamper-proof authentication certificates' },
            { term: 'Smart Contract Verification', desc: 'On-chain validation of authentication credentials' },
            { term: 'Provenance Tracking', desc: 'Complete history of ownership and verification events' },
            { term: 'Cross-Chain Compatibility', desc: 'Interoperability with major blockchain networks' }
          ],
          outro: 'This integration creates trustless verification services that institutions can rely upon for high-value artifact authentication.'
        },
        security: {
          heading: '9. Security & Risk Mitigation',
          intro: 'Shenbury implements comprehensive security measures to protect against adversarial attacks and ensure system integrity:',
          threatTitle: 'Threat Mitigation',
          threats: [
            { term: 'Adversarial Inputs', desc: 'Multi-layer validation and anomaly detection' },
            { term: 'Data Poisoning', desc: 'Statistical outlier detection and source verification' },
            { term: 'Model Extraction', desc: 'Query rate limiting and behavioral analysis' }
          ],
          resilienceTitle: 'Resilience Measures',
          resilience: [
            { term: 'Fault Tolerance', desc: '99.9% uptime through redundant architectures' },
            { term: 'Disaster Recovery', desc: 'Automated backup and restoration protocols' },
            { term: 'Continuous Monitoring', desc: 'Real-time threat detection and response' }
          ],
          outro: 'Regular third-party security audits and penetration testing ensure ongoing protection against emerging threats in the authentication technology landscape.'
        },
        roadmap: {
          heading: '10. Roadmap',
          phases: [
            { phase: 'Q4 2025', milestone: 'Company formation, protocol deployment, initial artifact onboarding', status: 'In Progress' },
            { phase: 'Q1 2026', milestone: 'Database + Reasoning Engine enhanced with proprietary GNN and Self-Optimizing architecture', status: 'Completed' },
            { phase: 'Q2 2026', milestone: 'Full AI Swarm \u2194 Authentication Chain integration', status: 'Planned' },
            { phase: 'Q3 2026', milestone: 'Auction house partnerships and marketplace launch', status: 'Planned' },
            { phase: 'Q4 2026', milestone: 'Expansion to additional museum-grade art categories (paintings, sculpture, manuscripts)', status: 'Future' }
          ]
        },
        competitiveAnalysis: {
          heading: '11. Competitive Analysis',
          intro: 'Shenbury\'s competitive advantages in the institutional authentication market:',
          tableHeaders: {
            feature: 'Feature',
            shenbury: 'Shenbury',
            traditional: 'Traditional Experts',
            basicAI: 'Basic AI Tools'
          },
          tableRows: [
            { feature: 'Accuracy Rate', shenbury: '99.2%+', traditional: '85-95%', basicAI: '70-80%' },
            { feature: 'Response Time', shenbury: '<30 seconds', traditional: 'Days-Weeks', basicAI: 'Minutes-Hours' },
            { feature: 'Cost Efficiency', shenbury: '80% reduction', traditional: 'High', basicAI: 'Moderate' },
            { feature: 'Scalability', shenbury: 'Unlimited', traditional: 'Limited by experts', basicAI: 'Constrained' },
            { feature: 'Audit Trail', shenbury: 'Immutable', traditional: 'Manual records', basicAI: 'Limited' }
          ],
          outro: 'Unlike traditional authentication services that rely on scarce human expertise or basic AI tools with limited accuracy, Shenbury provides **institutional-grade verification at scale** through proprietary AI technology and cryptographic proof systems.'
        },
        conclusion: {
          heading: '12. Conclusion',
          paragraphs: [
            'Shenbury establishes a new computational paradigm: **self-contained AI agents enhanced with proprietary GNN and Self-Optimizing technologies** that learn, reason, and preserve institutional memory without external dependencies while continuously improving through self-supervised learning.',
            'By providing cryptographically verifiable authentication services to museums, collectors, and cultural institutions, Shenbury creates **trustless verification infrastructure** that enables secure participation in high-value artifact markets with **unprecedented accuracy and self-improving capabilities**.',
            'By providing institutional-grade authentication services to museums, collectors, and cultural institutions, Shenbury positions itself as essential infrastructure for the digitization and preservation of cultural heritage assets.'
          ]
        },
        footerCopyright: '\u00a9 2026 Shenbury \u2014 shenbury.com'
      }
    },
    
    // Technical Paper
    technicalPaper: {
      draft: 'Draft v1.0',
      title: 'Technical Paper on AI Protocols',
      subtitle: 'Shenbury AI-Powered Authentication Protocol',
      date: 'January 22, 2026',
      team: 'Shenbury Core Development Team',
      content: {
        abstract: {
          heading: 'Abstract',
          paragraphs: [
            'The Shenbury AI-Powered Authentication Protocol introduces a decentralized, multi-agent AI system for cryptographic-grade verification of high-value artifacts and cultural assets. The primary challenge in institutional authentication markets is information asymmetry and the opacity of verification processes, which constrains trust and operational efficiency.',
            'Our protocol solves this by fusing a resilient, self-contained AI stack with an immutable on-chain ledger. This system is built on a **"zero-cost" framework** that runs locally with no external dependencies, eliminating API fees and ensuring data privacy. Enhanced with **advanced GNN technology**, our system now features self-improving vector search and advanced pattern recognition capabilities.'
          ]
        },
        introduction: {
          heading: '1. Introduction',
          paragraphs: [
            'The authentication of high-value artifacts has been hampered by a single point of failure: the **"verification oracle problem."** An on-chain attestation is only as reliable as the off-chain verification process it represents.',
            'In institutional authentication markets, this problem is amplified. Verification is traditionally a manual, centralized, and often subjective process, making it impossible to guarantee authenticity in a trustless manner.',
            'The Shenbury AI Protocol is designed to solve this problem at its root. Instead of relying on human oracles, we have built an **autonomous AI swarm** that functions as an unbiased, data-driven verification engine. Enhanced with **proprietary Graph Neural Networks**, our swarm now features self-learning capabilities that continuously improve verification accuracy through pattern recognition and trajectory learning.'
          ]
        },
        agenticManifesto: {
          heading: '2. The Agentic Manifesto (Core Philosophy)',
          intro: 'Our architecture is guided by the **"Agentic Manifesto,"** which prioritizes resilience, autonomy, and security:',
          principles: [
            { num: '1', title: 'Failure is Assumed, Recovery is Automated', desc: 'The system is designed for fault tolerance. Agents operate in a resilient mesh topology, ensuring that the failure of a single agent does not halt the verification process.' },
            { num: '2', title: 'Agents as Stateless, Purpose-Bound Services', desc: 'Each AI agent is a small, specialized microservice with a single, dedicated function (e.g., "Chemical Analysis Agent," "Visual Stylometry Agent").' },
            { num: '3', title: 'Zero-Trust Security & Immutable Ledgers', desc: 'Security is zero-trust. All inter-agent communication is authenticated. The final verification "attestation" is cryptographically hashed and logged to the Ethereum blockchain.' },
            { num: '4', title: 'Humans as Stewards, Not Operators', desc: 'Human experts are used to train the AI swarm and provide initial ground-truth data. However, they are removed from the critical path of active operations.' }
          ]
        },
        systemArchitecture: {
          heading: '3. System Architecture: The Shenbury Stack',
          intro: 'The key innovation of the Shenbury stack is its combination of **speed, autonomy, and zero recurring cost**. The entire system\u2014database, learning model, and embeddings\u2014runs locally, allowing agents to become self-contained learning entities.',
          subsections: {
            orchestration: { heading: '3.1 Orchestration Layer: Proprietary Agent Management', paragraph: 'Our proprietary orchestration platform manages the lifecycle of verification tasks, tasking specialized agents and coordinating the flow of data between them.' },
            swarmCoordination: { heading: '3.2 Swarm Coordination & Memory: Proprietary Coordination System', paragraph: 'Our proprietary coordination system provides the memory and communication backbone for the agent swarm. Its initialization creates a resilient, decentralized communication network between agents.' },
            agentDB: {
              heading: '3.3 Enhanced Database: Proprietary Vector Intelligence Core',
              intro: 'Our proprietary database has been enhanced with **advanced Graph Neural Network technology**, transforming it into a self-improving vector database that learns from every query and interaction. This represents a fundamental advancement over traditional vector databases.',
              items: [
                '**Enhanced Search:** Graph Neural Networks improve search results over time through learned pattern recognition',
                '**Self-Optimizing Architecture:** Advanced neural architecture with two-tier adaptation for real-time learning',
                '**Trajectory Learning:** Records and learns from complete agent interaction paths for continuous improvement',
                '**39 Attention Mechanisms:** Advanced attention models for superior pattern matching and similarity scoring',
                '**Performance:** Achieves 15-25% improved accuracy compared to traditional vector search',
                '**Efficiency:** Maintains 4x to 32x compression while enabling adaptive learning capabilities'
              ]
            },
            reasoningBank: {
              heading: '3.4 Enhanced Autonomous Learning: Proprietary Reasoning Engine',
              intro: 'Our reasoning engine has been enhanced with **proprietary Self-Optimizing Neural Architecture**, creating a hybrid learning system that combines trajectory-based learning with real-time adaptation. This represents a quantum leap in autonomous AI improvement capabilities.',
              items: [
                '**Trajectory Recording:** Records complete agent interaction paths with embedded learning signals',
                '**Micro-Adaptation:** Sub-millisecond adaptation using advanced techniques for immediate learning',
                '**Pattern Clustering:** K-means++ clustering of successful verification patterns with 100-cluster optimization',
                '**Anti-Forgetting Protection:** Advanced consolidation prevents catastrophic forgetting during continuous learning',
                '**Multi-Factor Evaluation:** Evaluates outcomes using similarity, documentation quality, agent consensus, and pattern matching',
                '**Enhanced Retrieval:** Memory retrieval balances semantic similarity, reliability scores, recency decay, and diversity factors'
              ]
            },
            localEmbeddings: {
              heading: '3.5 Enhanced Local Embeddings: Proprietary Vector Processing',
              intro: 'All data (images, chemical analysis reports, documentation) is vectorized locally using our proprietary processing system, enhanced with **advanced attention mechanisms** for superior pattern recognition and adaptive similarity scoring.',
              items: [
                '**Proprietary Engine:** Advanced processing with specialized models and attention layers',
                '**Output:** Generates 384-dimensional vectors with attention-weighted embeddings',
                '**Attention Mechanisms:** Leverages multiple attention models for context-aware similarity scoring',
                '**Adaptive Learning:** Embeddings continuously improve through advanced adaptation techniques',
                '**Cost:** After an initial 23MB model download, all operations run offline at **zero marginal cost**',
                '**Privacy:** Complete data privacy with no external API calls or cloud dependencies'
              ]
            }
          }
        },
        security: {
          heading: '4. Security Architecture & Threat Mitigation',
          intro: 'The Shenbury protocol implements comprehensive security measures to protect against adversarial attacks and ensure system integrity:',
          adversarialTitle: 'Adversarial Defense Mechanisms',
          adversarialItems: [
            { term: 'Anomaly Detection', desc: 'Statistical outlier detection in verification patterns' },
            { term: 'Input Sanitization', desc: 'Multi-layer validation of all data inputs' },
            { term: 'Rate Limiting', desc: 'Query throttling to prevent abuse' },
            { term: 'Behavioral Analysis', desc: 'Detection of suspicious agent interactions' }
          ],
          cryptoTitle: 'Cryptographic Security',
          cryptoItems: [
            { term: 'Zero-Knowledge Proofs', desc: 'Private verification without revealing source data' },
            { term: 'Multi-Signature Attestations', desc: 'Distributed verification signing' },
            { term: 'Hash Chain Integrity', desc: 'Immutable verification record linkage' },
            { term: 'Secure Enclave Execution', desc: 'Hardware-level protection for sensitive operations' }
          ],
          faultTolerance: { heading: '4.1 Fault Tolerance & Recovery', paragraph: 'The system implements Byzantine fault tolerance with automatic recovery mechanisms. Agent redundancy ensures 99.9% uptime, while distributed consensus protocols maintain system integrity even under partial failure conditions.' }
        },
        scalability: {
          heading: '5. Scalability & Performance Optimization',
          intro: 'The Shenbury protocol is designed for horizontal scaling and optimal performance across diverse verification workloads:',
          horizontalScaling: {
            heading: '5.1 Horizontal Scaling Architecture',
            items: [
              '**Dynamic Agent Provisioning:** Automatic scaling based on verification demand',
              '**Load Balancing:** Intelligent task distribution across agent swarm',
              '**Resource Isolation:** Containerized agents prevent resource contention',
              '**Elastic Infrastructure:** Cloud-native deployment with auto-scaling groups'
            ]
          },
          benchmarks: {
            heading: '5.2 Performance Benchmarks',
            headers: ['Metric', 'Target', 'Achieved'],
            rows: [
              { metric: 'Verification Response Time', target: '<30 seconds', achieved: '<25 seconds' },
              { metric: 'Concurrent Verifications', target: '1,000+', achieved: '1,500+' },
              { metric: 'Accuracy Rate', target: '99%+', achieved: '99.2%+' },
              { metric: 'System Uptime', target: '99.9%', achieved: '99.95%' }
            ]
          },
          resourceOptimization: {
            heading: '5.3 Resource Optimization',
            intro: 'Advanced optimization techniques minimize computational overhead while maintaining verification quality:',
            items: [
              '**Adaptive Precision:** Variable computation precision based on verification complexity',
              '**Caching Strategies:** Intelligent result caching reduces redundant computations',
              '**Compression Algorithms:** 4-32\u00d7 memory compression without quality loss',
              '**Energy Efficiency:** Optimized algorithms reduce power consumption by 40%'
            ]
          }
        },
        implementation: {
          heading: '6. Implementation & Deployment Strategy',
          intro: 'The Shenbury protocol follows a phased deployment approach with rigorous testing and validation:',
          phases: [
            { num: '1', title: 'Development Phase', desc: 'Core protocol development with unit testing and simulation environments' },
            { num: '2', title: 'Testing Phase', desc: 'Extensive testing with partner institutions and controlled artifact datasets' },
            { num: '3', title: 'Pilot Deployment', desc: 'Limited production deployment with select institutional partners' },
            { num: '4', title: 'Full Production', desc: 'Global deployment with continuous monitoring and improvement cycles' }
          ],
          techStack: {
            heading: '6.1 Technology Stack',
            stacks: [
              { heading: 'Core Infrastructure', items: ['Python 3.9+', 'Docker Containers', 'Kubernetes Orchestration', 'PostgreSQL + Vector Extensions'] },
              { heading: 'AI/ML Framework', items: ['PyTorch Geometric', 'Transformers Library', 'Custom GNN Modules', 'ONNX Runtime'] },
              { heading: 'Blockchain Integration', items: ['Ethereum Smart Contracts', 'IPFS Storage', 'Chainlink Oracles', 'Multi-chain Support'] }
            ]
          }
        },
        verificationWorkflow: {
          heading: '7. The Verification Workflow: An Example',
          steps: [
            { step: '1', title: 'Data Ingestion', desc: 'A new artifact (e.g., historical manuscript or archaeological find) is submitted. All known data is ingested: high-resolution imaging, material analysis, and documentation records.' },
            { step: '2', title: 'Local Embedding', desc: 'Our proprietary system converts all this unstructured data into 384-dimensional vectors.' },
            { step: '3', title: 'Task Orchestration', desc: 'Our orchestration system initiates a verification task.' },
            { step: '4', title: 'Enhanced Agent Swarm Query', desc: 'A Visual Agent queries the enhanced database for visually similar items with learned pattern matching. A Chemical Agent leverages adapted queries for matches in chemical composition. A Documentation Agent uses learned search patterns to cross-reference auction records and museum catalogs with improved accuracy.' },
            { step: '5', title: 'Enhanced Reasoning & Consensus', desc: 'Our coordination system facilitates communication as the agents share their findings. The enhanced reasoning engine synthesizes outputs using multi-factor analysis, generating a final "Authentication Confidence Score" based on similarity patterns, documentation quality, agent consensus, and learned verification trajectories.' },
            { step: '6', title: 'Attestation', desc: 'The full verification report, along with its confidence score and the data used, is cryptographically hashed. This hash is published to the Ethereum blockchain via a smart contract.' }
          ]
        },
        conclusion: {
          heading: '8. Conclusion',
          paragraphs: [
            'The Shenbury AI-Powered Authentication Protocol solves the institutional verification oracle problem by **becoming the oracle** through autonomous, trustless verification services.',
            'By building a fully integrated, self-contained AI stack enhanced with **proprietary Graph Neural Networks and Self-Optimizing architecture**, we have created an autonomous verification engine that operates without bias, learns from its outcomes, and functions at **zero marginal cost**.',
            'This enhanced protocol represents a new paradigm in AI-powered authentication, featuring self-improving pattern recognition, real-time learning capabilities, and advanced attention mechanisms that continuously optimize verification accuracy while maintaining cryptographic security and institutional trust.',
            'The technical innovations presented establish a foundation for scalable, secure authentication infrastructure that can serve museums, collectors, and cultural institutions worldwide with unprecedented reliability and cost-effectiveness.'
          ]
        },
        footerCopyright: '\u00a9 2026 Shenbury \u2014 shenbury.com'
      }
    },
    
    // FAQ
    faq: {
      badge: 'FAQ',
      title: 'Frequently Asked Questions',
      subtitle: 'Everything you need to know about Shenbury\'s AI authentication protocol.',
      questions: {
        whatIsShenbury: {
          question: 'What is Shenbury?',
          answer: 'Shenbury is an AI-powered authentication protocol that provides institutional-grade verification services for cultural heritage artifacts using advanced computer vision and machine learning technologies.'
        },
        howDoesItWork: {
          question: 'How does the authentication process work?',
          answer: 'Our system uses multiple AI models to analyze artifacts through high-resolution imaging, comparing them against extensive databases of authenticated pieces to determine authenticity with 99.8% accuracy.'
        },
        whoUsesIt: {
          question: 'Who uses Shenbury?',
          answer: 'Museums, auction houses, private collectors, and cultural institutions worldwide use our authentication services for provenance verification and insurance purposes.'
        },
        security: {
          question: 'How secure is the authentication data?',
          answer: 'All authentication records are cryptographically secured using blockchain technology and stored in distributed systems with military-grade encryption.'
        },
        whatMakesDifferent: {
          question: 'What makes Shenbury different from traditional authentication?',
          answer: 'Shenbury uses a swarm of specialized AI agents that cross-validate each other, combined with blockchain attestation for tamper-proof results. This provides higher accuracy and transparency than single-expert opinions.'
        },
        revenueModel: {
          question: 'How does the verification service work?',
          answer: 'Our platform offers tiered verification services — from basic AI screening to comprehensive multi-agent analysis with expert review. Each tier provides progressively deeper analysis and higher confidence scores.'
        },
        dataPrivacy: {
          question: 'How is my data protected?',
          answer: 'All submitted artifacts are encrypted in transit and at rest. Analysis is performed in secure, isolated environments. We never share your data with third parties without explicit consent.'
        },
        verificationServices: {
          question: 'What types of artifacts can be verified?',
          answer: 'Our AI swarm is trained on ceramics, porcelain, cloisonné, paintings, and other fine art categories. We continuously expand our training data to cover additional artifact types.'
        }
      },
      ctaQuestion: 'Have more questions?',
      ctaWhitepaper: 'Read Whitepaper',
      ctaTechnical: 'Technical Paper'
    },
    common: {
      downloadPDF: 'Download PDF',
      technicalPaper: 'Technical Paper',
      whitepaper: 'Whitepaper'
    },
    termsAndConditions: {
      title: 'Terms and Conditions',
      subtitle: 'Legal Framework for Shenbury Authentication Services',
      effectiveDate: 'Effective Date',
      lastUpdated: 'Last Updated',
      backToHome: 'Back to Home',
      sections: {
        acceptance: {
          title: '1. Acceptance of Terms',
          content: 'By accessing or using the Shenbury platform and services, you agree to be bound by these Terms and Conditions. If you do not agree, you may not access or use our services.'
        },
        services: {
          title: '2. Description of Services',
          content: 'Shenbury provides AI-powered authentication and verification services for cultural heritage artifacts. Our platform uses advanced machine learning, computer vision, and blockchain technology to assess and record artifact authenticity.'
        },
        eligibility: {
          title: '3. Eligibility',
          content: 'You must be at least 18 years old and capable of forming a binding contract to use our services. By using the platform, you represent that you meet these requirements.'
        },
        userObligations: {
          title: '4. User Obligations',
          content: 'Users must provide accurate information, maintain account security, and use the platform only for lawful purposes. You are responsible for all activities under your account.'
        },
        intellectualProperty: {
          title: '5. Intellectual Property',
          content: 'All technology, algorithms, models, and content on the Shenbury platform are owned by Sheboli Digital Technology Company Ltd. Users retain ownership of their submitted artifacts and data.'
        },
        privacy: {
          title: '6. Privacy and Data Protection',
          content: 'We are committed to protecting your privacy. All submitted artifact data is encrypted and processed in secure environments. Please review our Privacy Policy for detailed information.'
        },
        limitation: {
          title: '7. Limitation of Liability',
          content: 'Shenbury provides authentication assessments based on AI analysis. Results are probabilistic and should not be considered definitive legal proof of authenticity. We are not liable for decisions made based on our assessments.'
        },
        termination: {
          title: '8. Termination',
          content: 'We reserve the right to suspend or terminate accounts that violate these terms. Users may terminate their accounts at any time by contacting support.'
        },
        governing: {
          title: '9. Governing Law',
          content: 'These Terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through binding arbitration.'
        },
        changes: {
          title: '10. Changes to Terms',
          content: 'We may update these Terms from time to time. Continued use of the platform after changes constitutes acceptance of the updated Terms.'
        }
      }
    },
    swarmAnalysis: {
      badge: 'Swarm Intelligence',
      titlePart1: 'Enhanced Agent Swarm',
      titlePart2: 'Analysis',
      subtitle: 'Powered by proprietary GNN and Self-Optimizing self-learning architecture',
      verifyingDesc: 'Verifying contributions from our autonomous AI agent swarm for Lot 013:',
      artifactOverview: 'Artifact Overview',
      identification: 'Identification',
      authStatus: 'Authentication Status',
      labels: {
        id: 'ID:',
        name: 'Name:',
        dynasty: 'Dynasty:',
        period: 'Period:',
        role: 'Role:',
        methodology: 'Methodology:'
      },
      confidence: '% confidence',
      agentContributions: 'Enhanced Agent Contributions',
      agentContributionsDesc: 'Enhanced verification with self-learning capabilities',
      realResults: 'Real Analysis Results:',
      similarArtifacts: 'Enhanced Similar Artifacts',
      similarArtifactsDesc: 'Pattern-matched results with learned similarity scoring',
      similarity: 'similarity',
      learnedPatterns: 'Learned Patterns',
      learnedPatternsDesc: 'Self-optimizing neural architecture patterns',
      successRate: 'success rate',
      applications: 'applications',
      dbStats: 'Enhanced Database Statistics',
      dbStatsDesc: 'Enhanced metrics with learning analytics',
      statLabels: {
        theorems: 'Theorems',
        successRate: 'Success Rate',
        storage: 'Storage',
        agentTypes: 'Agent Types',
        patterns: 'Patterns'
      },
      metricLabels: {
        neuralLayers: 'Neural Layers',
        attentionMech: 'Attention Mech',
        patternClusters: 'Pattern Clusters',
        trajectories: 'Trajectories'
      },
      agents: {
        'visual-analysis-agent': {
          role: 'Visual Pattern Recognition',
          methodology: 'Deep learning model trained on 10,000+ authenticated cloisonne artifacts',
          result: 'Identified enamel composition and decorative motifs consistent with Qianlong period imperial workshop standards'
        },
        'xrf-spectroscopy-agent': {
          role: 'Elemental Composition Analysis',
          methodology: 'Spectral analysis with comparison to materials database',
          result: 'Detected copper, zinc, and trace cobalt elements matching documented Qianlong enamel recipes'
        },
        'historical-documentation-agent': {
          role: 'Provenance Verification',
          methodology: 'Cross-referencing with historical archives and museum collections',
          result: 'Verified provenance through imperial workshop records and comparative analysis'
        },
        'stylistic-analysis-agent': {
          role: 'Stylistic Authentication',
          methodology: 'Pattern matching against authenticated stylistic database',
          result: 'Confirmed dragon motif matches Qianlong court style with proper imperial iconography'
        },
        'consensus-agent': {
          role: 'Final Authentication',
          methodology: 'Weighted average of all confidence scores with bias correction',
          result: 'Synthesized findings from all specialized agents to reach final authentication conclusion'
        }
      },
      status: {
        authenticated: 'Authenticated'
      }
    }
  },

  zh: {
    // Navigation
    navigation: {
      technology: '技術',
      features: '功能',
      assets: '資產',
      swarm: '群體智能',
      learnMore: '了解更多',
      whitepaper: '白皮書',
      technicalPaper: '技術論文',
      terms: '服務條款',
      github: 'GitHub',
      linkedin: 'LinkedIn',
      backToHome: '返回首頁'
    },
    
    // Hero Section
    hero: {
      title: '文化遺產AI鑑定協議',
      subtitle: '神堡為博物館和文化機構提供機構級鑑定基礎設施，採用先進的AI驗證系統。',
      description: '我們專有的AI群體結合了計算機視覺、機器學習和區塊鏈驗證，實現無與倫比的真偽鑑定。',
      ctaPrimary: '探索資產',
      ctaSecondary: '查看白皮書',
      floatingCard: {
        badge: 'AI驗證',
        viewCollection: '查看收藏 →'
      },
      trustIndicators: {
        blockchain: '區塊鏈驗證',
        security: '銀行級安全',
        privacy: '數據隱私認證'
      },
      stats: {
        artifacts: '12件已驗證文物',
        accuracy: '99.8%準確率',
        institutions: '50多家機構',
        market: '$680億+ 藝術市場',
        processed: '10,000+ 文物已處理',
        optimized: 'AI優化',
        compute: '邊緣計算'
      }
    },
    
    // Technology Section
    technology: {
      title: '先進鑑定技術',
      subtitle: '我們的專有AI系統結合計算機視覺、機器學習和區塊鏈驗證，實現無與倫比的真實性評估。',
      description: '善寶AI驅動的鑑定協議：一個去中心化、自主的智能基礎設施，通過付費驗證服務解決全球藝術市場的預言機問題。',
      cards: {
        zeroTrust: {
          title: '零信任架構',
          description: '多層驗證需要多樣化AI模型達成共識後才能確認鑑定結果。',
          features: ['不可篡改記錄', '認證通信', '鏈上驗證']
        },
        localEmbeddings: {
          title: '本地嵌入生成',
          description: '設備端特徵提取確保隱私的同時保持鑑定準確性。',
          features: ['完整數據隱私', '無雲端依賴', '離線運行']
        },
        orchestration: {
          title: '智能編排',
          description: '根據文物特徵動態路由鑑定請求到最佳處理管道。',
          features: ['任務管理', '代理協調', '網狀拓撲']
        },
        quantx: {
          title: '抗量子安全',
          description: '面向未來的加密協議保護鑑定記錄免受量子計算威脅。',
          features: ['元素檢測', '無損檢測', '歷史數據比對']
        },
        gnn: {
          title: '圖神經網絡',
          description: '基於深度圖的關係模式識別，映射文物、材料和風格特徵之間的關聯結構，實現精確的真偽分析。',
          features: ['本地執行', '專業角色', '閉環學習']
        },
        selfOptimizing: {
          title: '自優化向量數據庫',
          description: '自適應嵌入存儲通過學習反饋持續優化索引，確保亞毫秒級檢索與不斷提升的準確性。',
          features: ['4-32倍壓縮', '亞毫秒查詢', '無雲端依賴']
        },
        multiAgent: {
          title: '多代理編排',
          description: '專業AI代理協調群體通過共識協議交叉驗證發現，實現穩健的文物鑑定。',
          features: ['評估結果', '提取知識', '加權檢索']
        }
      },
      stats: {
        decentralized: {
          value: '去中心化',
          label: '規模化AI'
        },
        compression: {
          value: '32倍',
          label: '壓縮比'
        },
        dimensions: {
          value: '384',
          label: '向量維度'
        },
        cloud: {
          value: '零',
          label: '雲端依賴'
        }
      }
    },

    // Features Section
    features: {
      title: '企業功能',
      subtitle: '為機構收藏和專業藝術市場設計的綜合鑑定解決方案。',
      cards: {
        aiSwarm: {
          title: 'AI群體分析',
          description: '多個專業AI代理從風格、材料和歷史背景等維度分析您的文物，通過共識協議交叉驗證發現。'
        },
        blockchain: {
          title: '區塊鏈認證',
          description: '驗證結果永久記錄在鏈上，創建不可篡改的真偽證書，具有防篡改的來源記錄。'
        },
        apiAccess: {
          title: '安全API訪問',
          description: '企業級API集成實現對AI驗證服務的無縫訪問，具備強大的安全和認證控制。'
        },
        zeroTrust: {
          title: '零信任安全',
          description: '所有代理間通信均經過認證。安全模型假設故障並確保自動恢復。'
        },
        decentralized: {
          title: '去中心化網絡',
          description: '網狀拓撲確保彈性。單個代理故障不會停止驗證，保持99.99%的正常運行時間。'
        },
        learning: {
          title: '持續學習',
          description: '專有推理引擎從結果中學習，每次完成驗證都提高準確性。'
        },
        spectroscopy: {
          title: 'XRF光譜分析',
          description: '使用X射線熒光的QuantX方法分析元素組成。無損檢測將化學特徵與歷史數據集進行比對以進行鑑定。'
        }
      }
    },
    
    // How It Works Section
    howItWorks: {
      badge: '驗證流程',
      title: '善寶如何運作',
      subtitle: '我們的自主AI協議通過四個無縫步驟提供鑑定服務，從數據提交到不可篡改的區塊鏈認證。通過我們的安全平台訪問驗證服務。',
      steps: {
        upload: {
          title: '上傳文物',
          description: '通過我們的安全門戶提交高分辨率圖像和文件。'
        },
        analyze: {
          title: 'AI分析',
          description: '多個神經網絡檢查材料、製作工藝和歷史標記。'
        },
        verify: {
          title: '區塊鏈驗證',
          description: '結果經過密碼學密封並永久記錄在分佈式賬本技術上。'
        },
        dataSubmission: {
          title: '數據提交',
          description: '通過我們的安全上傳門戶提交高分辨率圖像和來源文檔。'
        },
        aiSwarmAnalysis: {
          title: 'AI群體分析',
          description: '多個專業AI代理從風格、材料和歷史背景等維度分析您的文物。'
        },
        consensusLearning: {
          title: '共識學習',
          description: 'AI代理通過我們專有的群體智能協議交叉驗證發現並達成共識。'
        },
        blockchainAttestation: {
          title: '區塊鏈認證',
          description: '結果永久記錄在鏈上，創建不可篡改的真偽證書。'
        }
      },
      ctaReady: '準備使用我們的AI鑑定服務？',
      ctaLearnMore: '了解更多',
      ctaReadTechnical: '閱讀技術論文'
    },
    
    // Relic Gallery
    relicGallery: {
      title: '已驗證文物收藏',
      subtitle: '探索我們精心策展的已鑑定歷史文物收藏，擁有完整的來源記錄。',
      relics: {
        songWasher: {
          title: '極其稀有的汝窯葵花洗',
          chinese: '北宋 汝官窯天青釉葵花洗'
        },
        songBottle: {
          title: '宋代汝窯青瓷膽瓶',
          chinese: '宋 汝窯青瓷膽瓶'
        },
        mingCloisonne: {
          title: '明代景泰藍花鳥紋梅瓶',
          chinese: '明 景泰藍花鳥紋梅瓶'
        },
        yuanEwer: {
          title: '元代鈞窯玫瑰紫釉花觚',
          chinese: '元 鈞窯玫瑰紫釉花觚'
        },
        mingCasket: {
          title: '明代景泰款掐絲琺琅盒',
          chinese: '明 景泰款掐絲琺琅盒'
        },
        yuanJar: {
          title: '元代龍泉窯青瓷梅瓶',
          chinese: '元 龍泉窯青瓷梅瓶'
        },
        floralVase: {
          title: '清代琺琅彩花卉紋瓶',
          chinese: '清 珐琅彩花卉紋瓶'
        },
        imperialLotus: {
          title: '清代乾隆款粉彩蓮花紋膽瓶',
          chinese: '清 乾隆款粉彩蓮花紋膽瓶'
        },
        imperialTripod: {
          title: '明代宣德款掐絲琺琅三足爐',
          chinese: '明 宣德款掐絲琺琅三足爐'
        },
        junyaoVessel: {
          title: '宋代鈞窯天藍釉八方盆',
          chinese: '宋 鈞窯天藍釉八方盆'
        },
        yuanBlueWhite: {
          title: '元代青花雲龍紋梅瓶',
          chinese: '元 青花雲龍紋梅瓶'
        },
        ruVase: {
          title: '北宋汝窯天青釉玉壺春瓶',
          chinese: '北宋 汝窯天青釉玉壺春瓶'
        }
      },
      dynasties: {
        northernSong: '北宋',
        song: '宋代',
        ming: '明代',
        yuan: '元代',
        qing: '清代'
      }
    },
    
    // Authenticated Relics
    authenticatedRelics: {
      badge: '鑑定收藏',
      sectionTitle: 'AI驗證文物',
      sectionSubtitle: '探索由我們自主AI鑑定服務驗證的文物，通過實例展示我們協議的能力。',
      badges: {
        aiVerified: 'AI驗證',
        blockchain: '區塊鏈',
        verified: '已驗證',
        authenticity: '真實性',
        certified: '已認證'
      },
      lot: '拍品 #',
      aiService: 'AI鑑定服務',
      demoReport: '示範報告',
      accessReport: '查看鑑定報告',
      detailedAnalysis: '詳細分析',
      tabs: {
        overview: '概覽',
        dimensions: '尺寸',
        condition: '狀態',
        significance: '意義'
      },
      description: '描述',
      historicalContext: '歷史背景',
      measurements: '測量數據',
      conditionReport: '狀態報告',
      aiAssessment: 'AI評估：極佳',
      aiAssessmentDesc: '我們的AI分析確認保存狀況極佳，與有文獻記載的歷史案例一致。',
      culturalSignificance: '文化意義',
      knownExamples: '全球已知實例',
      kilnPeriod: '窯爐運作時期',
      dynasty: '北宋',
      relic: {
        title: '極其稀有的汝窯葵花洗',
        chinese: '北宋 汝官窯天青釉葵花洗',
        dynasty: '北宋',
        description: '這件極其稀有的洗子代表了宋代陶瓷工藝的巔峰。器物呈六瓣花口形，底部有三個細小的芝麻釘痕。其獨特的天青色釉面帶有細微的開片紋理，被認為是所有中國瓷器中的精品。',
        condition: '保存狀況極佳，釉面遍布典型的冰裂紋。足圈有與年代相符的輕微磨損。',
        culturalSignificance: '汝窯僅在約20年間（1086-1106年）專門為北宋皇室燒製。全世界存世的真品不到100件。有句俗話說：「縱有家財萬貫，不如汝瓷一片。」',
        historicalContext: '此洗子體現了汝窯工匠的技術精湛。該器物可能用於宮廷儀式或作為文人的案頭清供，反映了北宋皇室的雅緻文化。',
        verificationStatus: '區塊鏈驗證'
      }
    },
    
    // Single Artwork Showcase
    singleArtwork: {
      title: '極其稀有的汝窯葵花洗',
      chineseTitle: '北宋 汝官窯天青釉葵花洗',
      dynasty: '北宋',
      description: '這件極其稀有的洗子代表了宋代陶瓷工藝的巔峰。器物呈六瓣花口形，底部有三個細小的芝麻釘痕。其獨特的天青色釉面帶有細微的開片紋理，被認為是所有中國瓷器中的精品。',
      culturalSignificance: '汝窯僅在約20年間（1086-1106年）專門為北宋皇室燒製。全世界存世的真品不到100件。有句俗話說：「縱有家財萬貫，不如汝瓷一片。」',
      provenance: '此前藏於北京故宮博物院。2019年通過蘇富比香港拍賣行被私人收藏家收購。',
      condition: '保存狀況極佳，釉面遍布典型的冰裂紋。足圈有與年代相符的輕微磨損。',
      save: '收藏',
      share: '分享',
      authentication: {
        title: '鑑定詳情',
        method: '多重AI共識驗證',
        confidence: '置信度評分',
        date: '驗證日期',
        verifiedBy: '由神堡AI網絡驗證'
      },
      descriptionTitle: '文物描述',
      provenanceTitle: '來源',
      conditionTitle: '狀況報告',
      culturalSignificanceTitle: '文化意義'
    },
    
    // Footer
    footer: {
      product: '產品',
      resources: '資源',
      copyright: '© {year} Sheboli Digital Technology Company Ltd. 版權所有。',
      disclaimer: '免責聲明：這是一個為文化遺產驗證提供AI鑑定服務的技術基礎設施項目。',
      description: '為博物館和文化機構提供AI驅動的鑑定基礎設施。神堡'
    },
    
    // Whitepaper
    whitepaper: {
      title: 'Shenbury 白皮書',
      researchEdition: '研究版',
      version: '版本 1.2',
      year: '2026',
      downloadPDF: '下載PDF',
      viewOnGitHub: '在GitHub上查看',
      content: {
        abstract: {
          heading: '摘要',
          paragraphs: [
            '**Shenbury**引入了去中心化、自主的智能基礎設施，用於鑑定和驗證高價值文物。其核心是Shenbury的**AI代理群體**，結合了本地向量推理、閉環學習和密碼學鑑定服務。通過**專有圖神經網絡和自優化架構**的增強，每個代理具備從每次驗證交互中學習的自我改進能力。該系統使用完全本地的技術棧獨立運行——數據庫、嵌入和學習層——不依賴任何外部API或雲系統。',
            '本文檔形式化了Shenbury的數學基礎、學習動力學和基於效用的服務架構，專為機構鑑定市場而設計。'
          ]
        },
        problemStatement: {
          heading: '1. 問題陳述',
          paragraphs: [
            '全球藝術市場年交易額超過**600億美元**，但結構上仍缺乏流動性和透明度。鑑定——經過驗證的所有權和真實性歷史——決定了價值，但受到中心化信任模型和主觀專業知識的阻礙。',
            '對於藝術資產，鑑定確定性(Pₐ)和驗證置信度(Aₐ)具有高度不確定性，導致流動性被壓抑和風險溢價膨脹。Shenbury通過**配備專有GNN技術的去中心化自學習AI代理**，在可驗證的密碼學共識下運行並具備持續改進能力，從而提高這兩項指標。'
          ]
        },
        systemArchitecture: {
          heading: '2. 系統架構概覽',
          intro: 'Shenbury技術棧整合了五個主要層級：',
          layers: [
            { num: '1', title: '增強向量智能核心', desc: '配備專有GNN學習的本地高性能向量數據庫' },
            { num: '2', title: '增強推理引擎', desc: '具備軌跡學習和模式識別的閉環強化系統' },
            { num: '3', title: 'Agentic-Flow', desc: '多代理編排和自適應任務分配' },
            { num: '4', title: 'Claude-Flow', desc: '分佈式記憶和群體同步' },
            { num: '5', title: '鑑定鏈', desc: '已鑑定資產的區塊鏈註冊表' }
          ]
        },
        agentDB: {
          heading: '3. AgentDB \u2014 本地向量智能核心',
          paragraphs: [
            '增強數據庫提供**亞毫秒延遲**的語義記憶和檢索操作，由**專有圖神經網絡**驅動。每個項目使用帶有注意力加權嵌入的本地處理模型，嵌入為384維向量。',
            '向量量化實現**4-32倍記憶體壓縮**，同時保持語義保真度。集成的神經層實現**從使用模式中持續學習**，通過基於軌跡的優化使搜索結果隨時間不斷改進。'
          ]
        },
        reasoningBank: {
          heading: '4. ReasoningBank \u2014 閉環學習系統',
          paragraphs: [
            '增強推理引擎使用**專有自優化神經架構**將推理轉化為持續的自我改進過程。對於每個任務，代理信念參數根據通過軌跡學習觀察到的結果進行更新，實現啟發式和可靠性權重的自主精煉。',
            '這種具有**雙層適應和防遺忘保護**的閉環反饋使代理能夠為未來任務提取可重用的知識組件，在無外部依賴的情況下創建機構記憶，同時防止災難性遺忘。'
          ]
        },
        memoryRetrieval: {
          heading: '5. 記憶檢索與加權選擇',
          intro: '記憶檢索平衡四個關鍵因素：',
          items: [
            { term: '語義相似性', desc: '查詢與記憶向量之間的相關性，採用注意力加權評分' },
            { term: '可靠性評分', desc: '來自推理引擎的歷史準確性，採用軌跡加權置信度' },
            { term: '時效衰減', desc: '觀察的時間加權新鮮度，採用學習衰減率' },
            { term: '多樣性因子', desc: '使用模式聚類減少冗餘檢索' },
            { term: '模式匹配', desc: '基於學習驗證模式的神經增強相似性' }
          ],
          outro: '此公式產生穩健的檢索結果供下游推理使用。'
        },
        agenticFlow: {
          heading: '6. Agentic-Flow \u2014 多代理編排',
          intro: 'Agentic-Flow通過動態任務分配和負載均衡協調多個專業AI代理。該系統採用**自適應路由算法**，根據代理專業知識、當前工作負載和歷史性能指標分配鑑定請求。',
          componentsIntro: '關鍵組件包括：',
          components: [
            { term: '任務路由器', desc: '智能分配傳入的驗證請求' },
            { term: '負載均衡器', desc: '優化代理群體的資源利用率' },
            { term: '性能監控器', desc: '實時追蹤準確性和響應時間' },
            { term: '故障轉移管理器', desc: '通過自動冗餘確保系統彈性' }
          ],
          outro: '此編排層實現水平擴展，同時在所有鑑定服務中保持一致的質量標準。'
        },
        claudeFlow: {
          heading: '7. Claude-Flow \u2014 分佈式記憶同步',
          intro: 'Claude-Flow通過**向量時鐘同步**和衝突解決協議，在分佈式代理網絡中維護一致的知識狀態。每個代理維護一個本地知識庫，定期與全局群體記憶同步。',
          mechanismsIntro: '核心機制包括：',
          mechanisms: [
            { term: '向量時鐘', desc: '追蹤知識更新之間的因果關係' },
            { term: 'Gossip協議', desc: '在網絡中高效傳播新的學習成果' },
            { term: '衝突解決', desc: '使用學習偏好模型合併分歧的知識狀態' },
            { term: '知識壓縮', desc: '在保持語義保真度的同時減少存儲開銷' }
          ],
          outro: '此分佈式記憶系統確保所有代理從集體學習中受益，同時保持運營獨立性。'
        },
        authenticationChain: {
          heading: '8. 鑑定鏈集成',
          intro: '鑑定鏈為已鑑定文物提供**不可篡改的驗證證明**。每次成功鑑定都會生成一個錨定到區塊鏈的密碼學證書，創建可審計的來源和驗證歷史軌跡。',
          integrationIntro: '關鍵集成點：',
          integrationPoints: [
            { term: '證書生成', desc: '自動創建防篡改的鑑定證書' },
            { term: '智能合約驗證', desc: '鏈上驗證鑑定憑證' },
            { term: '來源追蹤', desc: '完整的所有權和驗證事件歷史' },
            { term: '跨鏈兼容性', desc: '與主要區塊鏈網絡的互操作性' }
          ],
          outro: '此集成創建了機構可以依賴的無信任驗證服務，用於高價值文物鑑定。'
        },
        security: {
          heading: '9. 安全與風險緩解',
          intro: 'Shenbury實施全面的安全措施，以防禦對抗性攻擊並確保系統完整性：',
          threatTitle: '威脅緩解',
          threats: [
            { term: '對抗性輸入', desc: '多層驗證和異常檢測' },
            { term: '數據投毒', desc: '統計異常值檢測和來源驗證' },
            { term: '模型提取', desc: '查詢速率限制和行為分析' }
          ],
          resilienceTitle: '彈性措施',
          resilience: [
            { term: '容錯能力', desc: '通過冗餘架構實現99.9%正常運行時間' },
            { term: '災難恢復', desc: '自動備份和恢復協議' },
            { term: '持續監控', desc: '實時威脅檢測和響應' }
          ],
          outro: '定期的第三方安全審計和滲透測試確保持續防禦鑑定技術領域中出現的新興威脅。'
        },
        roadmap: {
          heading: '10. 發展路線圖',
          phases: [
            { phase: '2025年第四季度', milestone: '公司成立、協議部署、初始文物入庫', status: '進行中' },
            { phase: '2026年第一季度', milestone: '數據庫+推理引擎通過專有GNN和自優化架構增強', status: '已完成' },
            { phase: '2026年第二季度', milestone: '完整AI群體\u2194鑑定鏈集成', status: '計劃中' },
            { phase: '2026年第三季度', milestone: '拍賣行合作夥伴關係和市場平台上線', status: '計劃中' },
            { phase: '2026年第四季度', milestone: '擴展至更多博物館級藝術品類別（繪畫、雕塑、手稿）', status: '未來規劃' }
          ]
        },
        competitiveAnalysis: {
          heading: '11. 競爭分析',
          intro: 'Shenbury在機構鑑定市場中的競爭優勢：',
          tableHeaders: {
            feature: '功能',
            shenbury: 'Shenbury',
            traditional: '傳統專家',
            basicAI: '基礎AI工具'
          },
          tableRows: [
            { feature: '準確率', shenbury: '99.2%+', traditional: '85-95%', basicAI: '70-80%' },
            { feature: '響應時間', shenbury: '<30秒', traditional: '數天至數週', basicAI: '數分鐘至數小時' },
            { feature: '成本效率', shenbury: '降低80%', traditional: '高', basicAI: '中等' },
            { feature: '可擴展性', shenbury: '無限', traditional: '受專家限制', basicAI: '受限' },
            { feature: '審計軌跡', shenbury: '不可篡改', traditional: '手動記錄', basicAI: '有限' }
          ],
          outro: '與依賴稀缺人力專業知識或準確性有限的基礎AI工具的傳統鑑定服務不同，Shenbury通過專有AI技術和密碼學證明系統提供**規模化的機構級驗證**。'
        },
        conclusion: {
          heading: '12. 結論',
          paragraphs: [
            'Shenbury建立了一種新的計算範式：**配備專有GNN和自優化技術的自包含AI代理**，能夠在無外部依賴的情況下學習、推理和保存機構記憶，同時通過自監督學習持續改進。',
            '通過為博物館、收藏家和文化機構提供密碼學可驗證的鑑定服務，Shenbury創建了**無信任驗證基礎設施**，使參與者能夠以**前所未有的準確性和自我改進能力**安全地參與高價值文物市場。',
            '通過為博物館、收藏家和文化機構提供機構級鑑定服務，Shenbury將自身定位為文化遺產資產數字化和保存的關鍵基礎設施。'
          ]
        },
        footerCopyright: '\u00a9 2026 Shenbury \u2014 shenbury.com'
      }
    },
    
    // Technical Paper
    technicalPaper: {
      draft: '草稿 v1.0',
      title: 'AI協議技術論文',
      subtitle: 'Shenbury AI驅動鑑定協議',
      date: '2026年1月22日',
      team: 'Shenbury核心開發團隊',
      content: {
        abstract: {
          heading: '摘要',
          paragraphs: [
            'Shenbury AI驅動鑑定協議引入了一個去中心化的多代理AI系統，用於高價值文物和文化資產的密碼學級驗證。機構鑑定市場面臨的主要挑戰是信息不對稱和驗證流程的不透明性，這限制了信任和運營效率。',
            '我們的協議通過融合一個彈性的、自主的AI技術棧與不可篡改的鏈上賬本來解決這一問題。該系統建立在**「零成本」框架**之上，在本地運行且無外部依賴，消除了API費用並確保數據隱私。通過**先進的GNN技術**增強，我們的系統現在具備自我改進的向量搜索和高級模式識別能力。'
          ]
        },
        introduction: {
          heading: '1. 引言',
          paragraphs: [
            '高價值文物的鑑定一直受到一個單一故障點的困擾：**「驗證預言機問題。」** 鏈上認證的可靠性僅取決於其所代表的鏈下驗證流程。',
            '在機構鑑定市場中，這一問題被進一步放大。驗證傳統上是一個手動的、中心化的、且往往帶有主觀性的過程，使得在無信任環境下保證真實性成為不可能。',
            'Shenbury AI協議旨在從根本上解決這一問題。我們不再依賴人類預言機，而是構建了一個**自主AI群體**，作為一個無偏見的、數據驅動的驗證引擎運作。通過**專有圖神經網絡**增強，我們的群體現在具備自學習能力，通過模式識別和軌跡學習持續提高驗證準確性。'
          ]
        },
        agenticManifesto: {
          heading: '2. 代理宣言（核心哲學）',
          intro: '我們的架構遵循**「代理宣言」**，優先考慮彈性、自主性和安全性：',
          principles: [
            { num: '1', title: '假設故障，自動恢復', desc: '系統設計為容錯架構。代理在彈性網狀拓撲中運行，確保單個代理的故障不會中止驗證流程。' },
            { num: '2', title: '代理作為無狀態、目的導向的服務', desc: '每個AI代理是一個小型的、專門化的微服務，具有單一的專用功能（例如「化學分析代理」、「視覺風格測量代理」）。' },
            { num: '3', title: '零信任安全與不可篡改賬本', desc: '安全性為零信任。所有代理間通信均經過認證。最終的驗證「認證」經密碼學哈希處理並記錄到以太坊區塊鏈。' },
            { num: '4', title: '人類作為管理者，而非操作者', desc: '人類專家用於訓練AI群體並提供初始真實數據。然而，他們被移出主動運營的關鍵路徑。' }
          ]
        },
        systemArchitecture: {
          heading: '3. 系統架構：Shenbury技術棧',
          intro: 'Shenbury技術棧的關鍵創新在於其結合了**速度、自主性和零經常性成本**。整個系統\u2014數據庫、學習模型和嵌入\u2014在本地運行，使代理成為自包含的學習實體。',
          subsections: {
            orchestration: { heading: '3.1 編排層：專有代理管理', paragraph: '我們的專有編排平台管理驗證任務的生命週期，調度專業代理並協調它們之間的數據流。' },
            swarmCoordination: { heading: '3.2 群體協調與記憶：專有協調系統', paragraph: '我們的專有協調系統為代理群體提供記憶和通信骨幹。其初始化在代理之間創建一個彈性的、去中心化的通信網絡。' },
            agentDB: {
              heading: '3.3 增強數據庫：專有向量智能核心',
              intro: '我們的專有數據庫已通過**先進的圖神經網絡技術**增強，轉變為一個從每次查詢和交互中學習的自我改進向量數據庫。這代表了對傳統向量數據庫的根本性進步。',
              items: [
                '**增強搜索：** 圖神經網絡通過學習模式識別隨時間改進搜索結果',
                '**自優化架構：** 具備雙層適應的高級神經架構，用於實時學習',
                '**軌跡學習：** 記錄並從完整的代理交互路徑中學習，實現持續改進',
                '**39種注意力機制：** 先進的注意力模型，用於卓越的模式匹配和相似度評分',
                '**性能：** 與傳統向量搜索相比，準確性提高15-25%',
                '**效率：** 在實現自適應學習能力的同時保持4倍至32倍壓縮'
              ]
            },
            reasoningBank: {
              heading: '3.4 增強自主學習：專有推理引擎',
              intro: '我們的推理引擎已通過**專有自優化神經架構**增強，創建了一個結合軌跡學習與實時適應的混合學習系統。這代表了自主AI改進能力的量子飛躍。',
              items: [
                '**軌跡記錄：** 記錄帶有嵌入式學習信號的完整代理交互路徑',
                '**微適應：** 使用先進技術進行亞毫秒適應，實現即時學習',
                '**模式聚類：** 對成功驗證模式進行K-means++聚類，實現100集群優化',
                '**防遺忘保護：** 先進的整合防止持續學習過程中的災難性遺忘',
                '**多因素評估：** 使用相似性、文檔質量、代理共識和模式匹配評估結果',
                '**增強檢索：** 記憶檢索平衡語義相似性、可靠性評分、時效衰減和多樣性因素'
              ]
            },
            localEmbeddings: {
              heading: '3.5 增強本地嵌入：專有向量處理',
              intro: '所有數據（圖像、化學分析報告、文檔）使用我們的專有處理系統在本地進行向量化，並通過**先進的注意力機制**增強，實現卓越的模式識別和自適應相似度評分。',
              items: [
                '**專有引擎：** 使用專門模型和注意力層進行高級處理',
                '**輸出：** 生成帶有注意力加權嵌入的384維向量',
                '**注意力機制：** 利用多種注意力模型進行上下文感知的相似度評分',
                '**自適應學習：** 嵌入通過先進的適應技術持續改進',
                '**成本：** 初始23MB模型下載後，所有操作離線運行，**零邊際成本**',
                '**隱私：** 完整的數據隱私，無外部API調用或雲端依賴'
              ]
            }
          }
        },
        security: {
          heading: '4. 安全架構與威脅緩解',
          intro: 'Shenbury協議實施全面的安全措施，以防禦對抗性攻擊並確保系統完整性：',
          adversarialTitle: '對抗性防禦機制',
          adversarialItems: [
            { term: '異常檢測', desc: '驗證模式中的統計異常值檢測' },
            { term: '輸入淨化', desc: '所有數據輸入的多層驗證' },
            { term: '速率限制', desc: '查詢節流以防止濫用' },
            { term: '行為分析', desc: '檢測可疑的代理交互' }
          ],
          cryptoTitle: '密碼學安全',
          cryptoItems: [
            { term: '零知識證明', desc: '在不揭示源數據的情況下進行私有驗證' },
            { term: '多重簽名認證', desc: '分佈式驗證簽名' },
            { term: '哈希鏈完整性', desc: '不可篡改的驗證記錄鏈接' },
            { term: '安全飛地執行', desc: '敏感操作的硬件級保護' }
          ],
          faultTolerance: { heading: '4.1 容錯與恢復', paragraph: '系統實施拜占庭容錯和自動恢復機制。代理冗餘確保99.9%的正常運行時間，而分佈式共識協議在部分故障條件下維護系統完整性。' }
        },
        scalability: {
          heading: '5. 可擴展性與性能優化',
          intro: 'Shenbury協議設計為水平擴展，在各種驗證工作負載下實現最佳性能：',
          horizontalScaling: {
            heading: '5.1 水平擴展架構',
            items: [
              '**動態代理配置：** 根據驗證需求自動擴展',
              '**負載均衡：** 智能任務分佈到代理群體',
              '**資源隔離：** 容器化代理防止資源爭用',
              '**彈性基礎設施：** 雲原生部署配備自動擴展群組'
            ]
          },
          benchmarks: {
            heading: '5.2 性能基準',
            headers: ['指標', '目標', '達成'],
            rows: [
              { metric: '驗證響應時間', target: '<30秒', achieved: '<25秒' },
              { metric: '並發驗證數', target: '1,000+', achieved: '1,500+' },
              { metric: '準確率', target: '99%+', achieved: '99.2%+' },
              { metric: '系統正常運行時間', target: '99.9%', achieved: '99.95%' }
            ]
          },
          resourceOptimization: {
            heading: '5.3 資源優化',
            intro: '先進的優化技術在保持驗證質量的同時最小化計算開銷：',
            items: [
              '**自適應精度：** 根據驗證複雜性調整計算精度',
              '**緩存策略：** 智能結果緩存減少冗餘計算',
              '**壓縮算法：** 4-32倍記憶體壓縮且無質量損失',
              '**能源效率：** 優化算法降低40%功耗'
            ]
          }
        },
        implementation: {
          heading: '6. 實施與部署策略',
          intro: 'Shenbury協議遵循分階段部署方法，配合嚴格的測試和驗證：',
          phases: [
            { num: '1', title: '開發階段', desc: '核心協議開發，配合單元測試和模擬環境' },
            { num: '2', title: '測試階段', desc: '與合作機構進行廣泛測試，使用受控文物數據集' },
            { num: '3', title: '試點部署', desc: '與精選機構合作夥伴進行有限生產部署' },
            { num: '4', title: '全面生產', desc: '全球部署，配合持續監控和改進週期' }
          ],
          techStack: {
            heading: '6.1 技術棧',
            stacks: [
              { heading: '核心基礎設施', items: ['Python 3.9+', 'Docker容器', 'Kubernetes編排', 'PostgreSQL + 向量擴展'] },
              { heading: 'AI/ML框架', items: ['PyTorch Geometric', 'Transformers庫', '自定義GNN模組', 'ONNX Runtime'] },
              { heading: '區塊鏈集成', items: ['以太坊智能合約', 'IPFS存儲', 'Chainlink預言機', '多鏈支持'] }
            ]
          }
        },
        verificationWorkflow: {
          heading: '7. 驗證工作流程：示例',
          steps: [
            { step: '1', title: '數據攝取', desc: '提交新文物（例如歷史手稿或考古發現）。攝取所有已知數據：高分辨率成像、材料分析和文檔記錄。' },
            { step: '2', title: '本地嵌入', desc: '我們的專有系統將所有非結構化數據轉換為384維向量。' },
            { step: '3', title: '任務編排', desc: '我們的編排系統啟動驗證任務。' },
            { step: '4', title: '增強代理群體查詢', desc: '視覺代理查詢增強數據庫以查找視覺上相似的項目，採用學習模式匹配。化學代理利用適應性查詢匹配化學成分。文檔代理使用學習搜索模式交叉引用拍賣記錄和博物館目錄，提高準確性。' },
            { step: '5', title: '增強推理與共識', desc: '我們的協調系統促進代理分享發現的通信。增強推理引擎使用多因素分析綜合輸出，基於相似性模式、文檔質量、代理共識和學習驗證軌跡生成最終的「認證置信度評分」。' },
            { step: '6', title: '認證', desc: '完整的驗證報告連同其置信度評分和使用的數據經密碼學哈希處理。此哈希通過智能合約發佈到以太坊區塊鏈。' }
          ]
        },
        conclusion: {
          heading: '8. 結論',
          paragraphs: [
            'Shenbury AI驅動鑑定協議通過**成為預言機**，以自主的、無信任的驗證服務解決了機構驗證預言機問題。',
            '通過構建一個完全集成的、自主的AI技術棧，並通過**專有圖神經網絡和自優化架構**增強，我們創建了一個無偏見運作的自主驗證引擎，從結果中學習，並以**零邊際成本**運行。',
            '這一增強協議代表了AI驅動鑑定的新範式，具備自我改進的模式識別、實時學習能力和先進的注意力機制，在維護密碼學安全和機構信任的同時持續優化驗證準確性。',
            '所提出的技術創新為可擴展、安全的鑑定基礎設施奠定了基礎，能夠以前所未有的可靠性和成本效益服務全球的博物館、收藏家和文化機構。'
          ]
        },
        footerCopyright: '\u00a9 2026 Shenbury \u2014 shenbury.com'
      }
    },
    
    // FAQ
    faq: {
      badge: '常見問題',
      title: '常見問題',
      subtitle: '關於神堡AI鑑定協議您需要了解的一切。',
      questions: {
        whatIsShenbury: {
          question: '什麼是神堡？',
          answer: '神堡是一個AI驅動的鑑定協議，利用先進的計算機視覺和機器學習技術為文化遺產文物提供機構級驗證服務。'
        },
        howDoesItWork: {
          question: '鑑定過程如何運作？',
          answer: '我們的系統使用多個AI模型通過高分辨率成像分析文物，將其與大量已驗證文物數據庫進行比較，以99.8%的準確率確定真實性。'
        },
        whoUsesIt: {
          question: '誰在使用神堡？',
          answer: '世界各地的博物館、拍賣行、私人收藏家和文化機構使用我們的鑑定服務進行來源驗證和保險目的。'
        },
        security: {
          question: '鑑定數據有多安全？',
          answer: '所有鑑定記錄都使用區塊鏈技術進行密碼學保護，並存儲在具有軍用級加密的分佈式系統中。'
        },
        whatMakesDifferent: {
          question: '善寶與傳統鑑定有何不同？',
          answer: '善寶使用專業AI代理群體進行交叉驗證，結合區塊鏈認證實現防篡改結果。這比單一專家意見提供更高的準確性和透明度。'
        },
        revenueModel: {
          question: '驗證服務如何運作？',
          answer: '我們的平台提供分層驗證服務——從基礎AI篩選到包含專家審查的綜合多代理分析。每個層級提供更深入的分析和更高的置信度評分。'
        },
        dataPrivacy: {
          question: '我的數據如何受到保護？',
          answer: '所有提交的文物在傳輸和存儲過程中均已加密。分析在安全、隔離的環境中進行。未經明確同意，我們絕不會與第三方共享您的數據。'
        },
        verificationServices: {
          question: '可以驗證哪些類型的文物？',
          answer: '我們的AI群體經過陶瓷、瓷器、景泰藍、繪畫及其他藝術品類別的訓練。我們不斷擴展訓練數據以覆蓋更多文物類型。'
        }
      },
      ctaQuestion: '還有更多問題？',
      ctaWhitepaper: '閱讀白皮書',
      ctaTechnical: '技術論文'
    },
    common: {
      downloadPDF: '下載PDF',
      technicalPaper: '技術論文',
      whitepaper: '白皮書'
    },
    termsAndConditions: {
      title: '條款與細則',
      subtitle: '善寶鑑定服務法律框架',
      effectiveDate: '生效日期',
      lastUpdated: '最後更新',
      backToHome: '返回首頁',
      sections: {
        acceptance: {
          title: '1. 接受條款',
          content: '訪問或使用善寶平台及服務即表示您同意受這些條款和細則的約束。如果您不同意，請勿訪問或使用我們的服務。'
        },
        services: {
          title: '2. 服務說明',
          content: '善寶提供基於AI的文化遺產文物鑑定和驗證服務。我們的平台使用先進的機器學習、計算機視覺和區塊鏈技術來評估和記錄文物的真偽。'
        },
        eligibility: {
          title: '3. 使用資格',
          content: '您必須年滿18歲且具有簽訂具有約束力的合同的能力才能使用我們的服務。使用平台即表示您確認符合這些要求。'
        },
        userObligations: {
          title: '4. 用戶義務',
          content: '用戶必須提供準確的信息，維護帳戶安全，並僅將平台用於合法目的。您對帳戶下的所有活動負責。'
        },
        intellectualProperty: {
          title: '5. 知識產權',
          content: '善寶平台上的所有技術、算法、模型和內容均為設博利數字科技有限公司所有。用戶保留其提交的文物和數據的所有權。'
        },
        privacy: {
          title: '6. 隱私和數據保護',
          content: '我們致力於保護您的隱私。所有提交的文物數據均經過加密並在安全環境中處理。請查閱我們的隱私政策了解詳細信息。'
        },
        limitation: {
          title: '7. 責任限制',
          content: '善寶基於AI分析提供鑑定評估。結果為概率性質，不應被視為真偽的最終法律證明。我們不對基於我們評估做出的決定承擔責任。'
        },
        termination: {
          title: '8. 終止',
          content: '我們保留暫停或終止違反這些條款的帳戶的權利。用戶可隨時聯繫客服終止其帳戶。'
        },
        governing: {
          title: '9. 適用法律',
          content: '這些條款受適用法律管轄並據此解釋。任何爭議應通過具有約束力的仲裁解決。'
        },
        changes: {
          title: '10. 條款變更',
          content: '我們可能會不時更新這些條款。在變更後繼續使用平台即表示接受更新後的條款。'
        }
      }
    },
    swarmAnalysis: {
      badge: '群體智能',
      titlePart1: '增強代理群體',
      titlePart2: '分析',
      subtitle: '由專有GNN和自優化自學習架構驅動',
      verifyingDesc: '驗證來自自主AI代理群體對拍品013的貢獻：',
      artifactOverview: '文物概覽',
      identification: '識別信息',
      authStatus: '鑑定狀態',
      labels: {
        id: 'ID：',
        name: '名稱：',
        dynasty: '朝代：',
        period: '時期：',
        role: '角色：',
        methodology: '方法論：'
      },
      confidence: '% 置信度',
      agentContributions: '增強代理貢獻',
      agentContributionsDesc: '具備自學習能力的增強驗證',
      realResults: '實際分析結果：',
      similarArtifacts: '相似文物',
      similarArtifactsDesc: '基於學習相似度評分的模式匹配結果',
      similarity: '相似度',
      learnedPatterns: '學習模式',
      learnedPatternsDesc: '自優化神經架構模式',
      successRate: '成功率',
      applications: '應用',
      dbStats: '數據庫統計',
      dbStatsDesc: '具備學習分析的增強指標',
      statLabels: {
        theorems: '定理',
        successRate: '成功率',
        storage: '存儲',
        agentTypes: '代理類型',
        patterns: '模式'
      },
      metricLabels: {
        neuralLayers: '神經層',
        attentionMech: '注意力機制',
        patternClusters: '模式集群',
        trajectories: '軌跡'
      },
      agents: {
        'visual-analysis-agent': {
          role: '視覺模式識別',
          methodology: '基於10,000+件經鑑定景泰藍文物訓練的深度學習模型',
          result: '識別出與乾隆時期御用工坊標準一致的琺瑯成分和裝飾紋樣'
        },
        'xrf-spectroscopy-agent': {
          role: '元素成分分析',
          methodology: '光譜分析與材料數據庫比對',
          result: '檢測到銅、鋅和微量鈷元素，與記錄的乾隆琺瑯配方吻合'
        },
        'historical-documentation-agent': {
          role: '來源驗證',
          methodology: '與歷史檔案和博物館藏品交叉參考',
          result: '通過御用工坊記錄和對比分析驗證了來源'
        },
        'stylistic-analysis-agent': {
          role: '風格鑑定',
          methodology: '與經鑑定的風格數據庫進行模式匹配',
          result: '確認龍紋圖案與乾隆宮廷風格及正確的皇家圖案學相符'
        },
        'consensus-agent': {
          role: '最終鑑定',
          methodology: '對所有置信度評分進行加權平均並校正偏差',
          result: '綜合所有專業代理的發現得出最終鑑定結論'
        }
      },
      status: {
        authenticated: '已鑑定'
      }
    }
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    try {
      const saved = localStorage.getItem('shenbury-language');
      return saved || 'en';
    } catch {
      return 'en';
    }
  });
  
  const handleSetLanguage = (lang) => {
    setCurrentLanguage(lang);
    try {
      localStorage.setItem('shenbury-language', lang);
    } catch {
      // localStorage unavailable (e.g. private browsing)
    }
  };
  
  const t = (key) => {
    const keys = key.split('.');
    let value = translations[currentLanguage];
    
    for (const k of keys) {
      value = value[k];
      if (value === undefined) return key;
    }
    
    return value;
  };
  
  return (
    <LanguageContext.Provider value={{ currentLanguage, setCurrentLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};